import { StockIssueRequest } from "../../../models/StockIssueRequest/StockIssueRequest";
import { StockCheckRequestStatus } from "../../../models/Stock/Stock";
import { OrderProduct } from "../../../models/Order/OrderProduct";

export interface UnpaidSampleProductSirCoverage {
  hasPendingSir: boolean;
  hasApprovedSir: boolean;
}

export function getUnpaidSampleSirCoverageByProduct(
  orderId: number,
  requests: StockIssueRequest[],
): Map<number, UnpaidSampleProductSirCoverage> {
  const map = new Map<number, UnpaidSampleProductSirCoverage>();

  requests.forEach((request) => {
    if (request.sir_flow !== "sample") return;
    if (Number(request.rejected) === 1 || Number(request.cancelled) === 1) {
      return;
    }

    const isPending =
      Number(request.approved) !== 1 &&
      Number(request.auto_approved) !== 1 &&
      Number(request.rejected) !== 1 &&
      Number(request.cancelled) !== 1;

    const isApproved =
      Number(request.approved) === 1 || Number(request.auto_approved) === 1;

    (request.order_products_mapping ?? []).forEach((line) => {
      if (Number(line.order_id) !== Number(orderId)) return;

      const productId = Number(line.product_id);
      const existing = map.get(productId) ?? {
        hasPendingSir: false,
        hasApprovedSir: false,
      };

      if (isPending) {
        existing.hasPendingSir = true;
      }

      if (isApproved && Number(line.quantity ?? 0) > 0) {
        existing.hasApprovedSir = true;
      }

      map.set(productId, existing);
    });
  });

  return map;
}

export function isProductResolvedViaApprovedSir(
  productId: number,
  coverage: UnpaidSampleProductSirCoverage | undefined,
): boolean {
  return Boolean(coverage?.hasApprovedSir);
}

export function hasPendingSampleSirOnOrder(
  orderId: number,
  requests: StockIssueRequest[],
): boolean {
  return requests.some((request) => {
    if (request.sir_flow !== "sample") return false;
    if (Number(request.rejected) === 1 || Number(request.cancelled) === 1) {
      return false;
    }

    const isPending =
      Number(request.approved) !== 1 &&
      Number(request.auto_approved) !== 1 &&
      Number(request.rejected) !== 1 &&
      Number(request.cancelled) !== 1;

    if (!isPending) return false;

    return (request.order_products_mapping ?? []).some(
      (line) => Number(line.order_id) === Number(orderId),
    );
  });
}

export function shouldShowUnpaidSampleOrderHint(params: {
  orderId?: number;
  products: OrderProduct[];
  sampleSirRequests: StockIssueRequest[];
}): boolean {
  if (!params.orderId) return true;

  const coverageByProduct = getUnpaidSampleSirCoverageByProduct(
    params.orderId,
    params.sampleSirRequests,
  );

  if (
    Array.from(coverageByProduct.values()).some((row) => row.hasPendingSir)
  ) {
    return true;
  }

  return params.products.some((product) => {
    const coverage = coverageByProduct.get(product.id);
    if (isProductResolvedViaApprovedSir(product.id, coverage)) {
      return false;
    }

    return (
      product.stock_check_status !== StockCheckRequestStatus.CLOSED
    );
  });
}

export function getUnpaidSampleOrderHintMessage(params: {
  orderId?: number;
  products: OrderProduct[];
  sampleSirRequests: StockIssueRequest[];
  isInventoryLoading: boolean;
  anyInWarehouse: boolean;
  allLinesCovered: boolean;
}): string {
  if (!params.orderId) {
    return "When warehouse stock is sufficient, raise a Stock Issue Req from the Unpaid Sample Req section. If stock is unavailable, use the usual Project → PR/PO flow.";
  }

  if (params.isInventoryLoading) {
    return "Checking Swageazy warehouse stock for this order…";
  }

  const coverageByProduct = getUnpaidSampleSirCoverageByProduct(
    params.orderId,
    params.sampleSirRequests,
  );

  if (
    Array.from(coverageByProduct.values()).some((row) => row.hasPendingSir)
  ) {
    return "Some products have pending stock issue requests. Resolve them (approve or reject) before raising stock check or approving a project request for the remaining lines.";
  }

  if (params.allLinesCovered && params.anyInWarehouse) {
    return "Swageazy inventory can cover this order. Use Stock Issue Req (Unpaid Sample Req section below) to issue stock without creating a project.";
  }

  if (params.anyInWarehouse) {
    return "Some lines have partial or no warehouse stock. Issue available qty via Stock Issue Req; for the shortfall, raise stock check for those products only, then continue with Project → PR/PO.";
  }

  return "No Swageazy warehouse stock for these products. Raise stock check for the products going via PR/PO, then continue with the usual Project → PR/PO sourcing flow.";
}
